interface Feature {
  title: string;
  desc: string;
}

interface Solucion {
  slug: string;
  nombre: string;
  icon: string;
  tagline: string;
  desc: string;
  color: string;
  features: Feature[];
  sectores: string[];
  panelType: string;
  /** SEO keywords específicas para Google */
  seoKeywords: string[];
  /** Descripción larga para meta description */
  seoDescription: string;
}

export const SOLUCIONES: Solucion[] = [
  {
    slug: 'reservas',
    nombre: 'Sistema de Reservas',
    icon: 'Calendar',
    tagline: 'Tu agenda. En piloto automático.',
    desc: 'Sistema de reservas online completo: calendario visual, confirmaciones automáticas, pagos y recordatorios.',
    color: 'blue',
    features: [
      { title: 'Calendario visual', desc: 'Vista diaria/semanal/mensual para ti y tu equipo' },
      { title: 'Reservas 24/7', desc: 'Tus clientes reservan cuando quieren, sin llamadas' },
      { title: 'Confirmaciones automáticas', desc: 'Email y WhatsApp al instante' },
      { title: 'Lista de espera', desc: 'Cancela alguien → el sistema llena el hueco solo' },
      { title: 'Selección de profesional', desc: 'El cliente elige con quién quiere ser atendido' },
      { title: 'Cobro anticipado', desc: 'Señal o pago total en el momento de reservar' },
    ],
    sectores: ['hosteleria', 'clinicas', 'peluqueria', 'formacion'],
    panelType: 'reservas',
    seoKeywords: ['sistema reservas online', 'software reservas negocio', 'calendario reservas web', 'agenda online citas', 'reservas automáticas', 'cobro anticipado reserva', 'confirmaciones WhatsApp reservas', 'lista espera automática', 'reservas 24/7', 'app reservas a medida'],
    seoDescription: 'Sistema de reservas online a medida: calendario visual, confirmaciones por WhatsApp, cobro anticipado y lista de espera inteligente. Tus clientes reservan 24/7.',
  },
  {
    slug: 'pagos',
    nombre: 'Pasarelas de Pago',
    icon: 'CreditCard',
    tagline: 'Cobra online. Siempre.',
    desc: 'Integración de pagos con Stripe, Bizum y TPV virtual. Facturación automática incluida.',
    color: 'green',
    features: [
      { title: 'Stripe & Bizum', desc: 'Métodos de pago que usan tus clientes a diario' },
      { title: 'Facturación automática', desc: 'PDF generado y enviado al cliente tras el pago' },
      { title: 'Suscripciones', desc: 'Cobro recurrente para servicios mensuales' },
      { title: 'Pagos partidos', desc: 'Señal + resto o pago aplazado' },
      { title: 'Panel de cobros', desc: 'Historial, reembolsos y disputas desde un panel' },
      { title: 'Cumplimiento PSD2', desc: 'Autenticación fuerte y 3D Secure integrado' },
    ],
    sectores: ['hosteleria', 'clinicas', 'peluqueria', 'ecommerce', 'formacion'],
    panelType: 'pagos',
    seoKeywords: ['pasarela pago online negocio', 'cobro online Stripe', 'cobro Bizum negocio', 'facturación automática', 'TPV virtual', 'pago recurrente suscripción', 'integración pagos web', 'cobro online a medida', 'PSD2 pago seguro', 'software cobros autónomos'],
    seoDescription: 'Pasarelas de pago online para negocios: Stripe, Bizum y TPV virtual integrado. Facturación automática, suscripciones y cobro anticipado. Cumplimiento PSD2.',
  },
  {
    slug: 'crm',
    nombre: 'CRM a Medida',
    icon: 'Users',
    tagline: 'Conoce a cada cliente. Siempre.',
    desc: 'CRM diseñado para tu operativa real. Historial, seguimiento, automatizaciones y métricas.',
    color: 'indigo',
    features: [
      { title: 'Ficha de cliente', desc: 'Historial completo: visitas, pagos, preferencias' },
      { title: 'Pipeline personalizado', desc: 'Etapas adaptadas a tu proceso de venta' },
      { title: 'Tareas automáticas', desc: 'Follow-ups y recordatorios sin esfuerzo manual' },
      { title: 'Segmentación', desc: 'Listas por comportamiento, fecha, producto o valor' },
      { title: 'Integración email', desc: 'Envíos masivos con tasa de apertura y clics' },
      { title: 'Exportación', desc: 'Tus datos, siempre exportables a Excel/CSV' },
    ],
    sectores: ['gestoria', 'clinicas', 'ecommerce', 'formacion'],
    panelType: 'crm',
    seoKeywords: ['CRM a medida', 'CRM para pymes', 'software gestión clientes', 'CRM español', 'pipeline ventas personalizado', 'seguimiento clientes', 'CRM con automatizaciones', 'gestión leads', 'CRM integrado email', 'software CRM negocio'],
    seoDescription: 'CRM a medida para tu negocio: ficha de cliente completa, pipeline personalizado, follow-ups automáticos, segmentación y métricas de conversión. Sin complejidad innecesaria.',
  },
  {
    slug: 'automatizaciones',
    nombre: 'Automatizaciones',
    icon: 'Zap',
    tagline: 'Tu negocio trabaja. Tú descansas.',
    desc: 'Flujos automáticos de email, WhatsApp y tareas. Sin código, sin esfuerzo.',
    color: 'violet',
    features: [
      { title: 'Email flows', desc: 'Bienvenida, follow-up, reactivación automáticos' },
      { title: 'WhatsApp Business', desc: 'Recordatorios y confirmaciones por WhatsApp' },
      { title: 'Triggers por comportamiento', desc: 'Si X ocurre → Y pasa automáticamente' },
      { title: 'Integraciones externas', desc: 'Conectamos con tus herramientas actuales' },
      { title: 'Notificaciones internas', desc: 'Alerta a tu equipo cuando algo requiere atención' },
      { title: 'Logs y métricas', desc: 'Ve cuántos flows se ejecutan y qué resultado dan' },
    ],
    sectores: ['hosteleria', 'gestoria', 'ecommerce', 'formacion'],
    panelType: 'automatizaciones',
    seoKeywords: ['automatización negocio', 'automatizar WhatsApp Business', 'email marketing automático', 'flujos automatización', 'automatizar tareas negocio', 'integración herramientas', 'notificaciones automáticas', 'workflows sin código', 'automatización procesos pymes', 'software automatización España'],
    seoDescription: 'Automatizaciones para tu negocio: email flows, WhatsApp Business, triggers por comportamiento e integraciones con tus herramientas. Tu negocio trabaja solo.',
  },
  {
    slug: 'web',
    nombre: 'Web de Captación',
    icon: 'Globe',
    tagline: 'Tu escaparate digital. Que vende.',
    desc: 'Landing pages y webs construidas para captar clientes y agendar citas — conectadas a tus automatizaciones.',
    color: 'sky',
    features: [
      { title: 'Diseño orientado a conversión', desc: 'CTAs, formularios y flujos diseñados para que el visitante contacte' },
      { title: 'Carga ultrarrápida', desc: 'Sub-1s para que ningún cliente se vaya antes de ver tu oferta' },
      { title: 'Integración con agenda', desc: 'El botón "Reservar" abre directamente tu calendario en tiempo real' },
      { title: 'Formularios con CRM', desc: 'Cada lead va directo a tu panel de gestión y activa el flujo automático' },
      { title: 'Diseño mobile-first', desc: 'Perfecta en móvil, tablet y escritorio' },
      { title: 'Analytics', desc: 'Métricas de visitantes, leads y conversión desde el día 1' },
    ],
    sectores: ['gestoria', 'construccion', 'ecommerce', 'formacion'],
    panelType: 'web',
    seoKeywords: ['web de captación', 'landing page negocio', 'página web que vende', 'diseño web conversión', 'web rápida negocio', 'landing page con formulario CRM', 'web mobile-first', 'diseño web a medida', 'web para captar clientes', 'página web profesional empresa'],
    seoDescription: 'Webs y landing pages diseñadas para captar clientes: CTAs optimizados, carga ultrarrápida, integración con agenda y CRM. Tu escaparate digital que convierte.',
  },
  {
    slug: 'dashboard',
    nombre: 'Dashboard & Analytics',
    icon: 'BarChart3',
    tagline: 'Decide con datos. No con intuición.',
    desc: 'Panel de control en tiempo real con las métricas que importan a tu negocio.',
    color: 'orange',
    features: [
      { title: 'KPIs en tiempo real', desc: 'Ingresos, conversión, clientes activos y más' },
      { title: 'Gráficos interactivos', desc: 'Filtra por período, producto, profesional o canal' },
      { title: 'Alertas automáticas', desc: 'Notificación si algo cae por debajo del umbral' },
      { title: 'Comparativas', desc: 'Esta semana vs semana anterior, mes vs mes' },
      { title: 'Exportación', desc: 'Descarga informes en PDF o Excel cuando quieras' },
      { title: 'Acceso por roles', desc: 'Diferente vista para propietario, gerente y empleado' },
    ],
    sectores: ['hosteleria', 'clinicas', 'construccion', 'ecommerce'],
    panelType: 'dashboard',
    seoKeywords: ['dashboard negocio', 'panel control métricas', 'analytics negocio tiempo real', 'KPIs empresa', 'software métricas', 'informes automáticos negocio', 'dashboard a medida', 'business intelligence pymes', 'panel analítica ventas', 'cuadro mando negocio'],
    seoDescription: 'Dashboard y analytics a medida: KPIs en tiempo real, gráficos interactivos, alertas automáticas y exportación de informes. Toma decisiones con datos, no con intuición.',
  },
];
